/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the Folder's content Page
 * The URI keyword would have to match something like 'mon-dossier'
 * It will show by default a list of new published documents
 * but can then be switched to a list of sent documents
 * or a list of published documents within a specific chemise
 */

// Imports
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-regular-svg-icons';
import { faDownload, faPrint } from '@fortawesome/free-solid-svg-icons';

// Interfaces
import { PublicCabinet } from '../../interfaces/ICabinets';
import { Chemise, Dossier, Mission } from '../../interfaces/IDossiers';
import { Document } from '../../interfaces/IDocuments';
import { AlertType, FolderRenderableComponent, SortOrder, SortType, TableRow } from '../../interfaces/IUtils';

// Sides
import { Header } from '../sides/Header';
import { SideBar } from '../sides/SideBar';

// Utils
import { ErrorForUsr, displayAlert, signOut } from '../utils/Errors';
import { ApiCallConstructor, HTTPResponse, downloadDocument, fetchUpcomingActions, fetchNewDocs } from '../utils/ApiCallConstructor';
import { dateToDDMMYYYY, getDossierNameFormated, getExtFromFilename, getTypeImg } from '../utils/Format';

// subpages 
import { Advertising } from '../subpages/Advertising';
import { ChemiseDetails } from '../subpages/ChemiseDetails';
import { SentDocuments } from '../subpages/SentDocuments';

// Buroclic adaptive components
import { BCTable } from '../buroclic/BCTable';
import { Action } from '../../interfaces/IActions';
import { printElement } from '../utils/Miscellaneous';
import { EventsTable } from '../subpages/EventsTable';


export const FolderPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const tmpDossier = JSON.parse(localStorage.getItem('currentFolder') || "{}");

    const cabinet = JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet;

    const [dossier, setDossier] = useState(state?.dossier || tmpDossier as Dossier);
    document.title = 'Espace client | ' + dossier.DOS_TITRE;

    const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(false);
    const [missionsExpanded, setMissionsExpanded] = useState<boolean>(false);
    const [chemisesExpanded, setChemisesExpanded] = useState<boolean>(false);

    // New published documents
    const [isDownloading, setIsDownloading] = useState(false);
    const [newDocuments, setNewDocuments] = useState<Document[]>(state?.newDocuments || []);
    const [newDocsDossier, setNewDocsDossier] = useState<Document[]>([]);

    // New published actions relative states/functions
    const [upcomingActions, setUpcomingActions] = useState<Action[]>([]);
    const [upcomingActionsDossier, setUpcomingActionsDossier] = useState<Action[]>([]);

    // Current selected data
    const [topChemise, setTopChemise] = useState({} as Chemise); // top treeview level chemise
    const [chemise, setChemise] = useState({} as Chemise);
    const [mission, setMission] = useState({} as Mission);

    // All missions
    const [missions, setMissions] = useState([] as Array<Mission>);

    // All chemises
    const [chemises, setChemises] = useState([] as Array<Chemise>);
    const [treeviewChemises, setTreeviewChemises] = useState([] as Array<Chemise>);
    const [topLevelChemises, setTopLevelChemises] = useState([] as Array<Chemise>);


    // Get initial data
    useEffect(() => {
        // Handle Dossier issue
        if (!dossier.DOS_REF) {
            displayAlert({ message: 'Aucun dossier n\'a été sélectionné', type: AlertType.error });
            navigate(`/accueil`);
        }

        // Get new documents from BACK or localstorage, otherwise, display error and handle session expiration
        fetchNewDocs().then((documents: Document[]) => setNewDocuments(documents))
            .catch((err: any) => {
                displayAlert({ message: err.error, type: AlertType.error })
                if (err.error === ErrorForUsr.NotSignedIn)
                    signOut((cabAlias) => navigate(`/${cabAlias}/login`));
            });


        // Get chemises with published elements
        // get chemises for this specific dossier
        const fetchChemises = async () => {
            try {
                const getChemisesResponse: HTTPResponse = await toast.promise(
                    ApiCallConstructor({
                        endpoint: `/me/dossiers/${dossier.DOS_REF}/chemises`,
                        method: 'GET',
                    }),
                    {
                        pending: `Récupération des chemises en cours`,
                        error: { render({ data }) { return `Erreur lors de la récupération des chemises : ${(data as HTTPResponse).error}` } },
                    });

                // Store chemises
                const tmpChemises: Array<Chemise> = getChemisesResponse.result as Array<Chemise>;
                setChemises(tmpChemises);
            } catch (err: any) {
                // Handle redirection for session expiration
                if (err.error === ErrorForUsr.NotSignedIn)
                    signOut((cabAlias) => navigate(`/${cabAlias}/login`));

                console.error(err.error)
            }
        }
        fetchChemises().catch((err) => displayAlert({ message: err.error, type: AlertType.error })); // Retrieve Chemises


        // Get missions with published elements
        // get missions for this specific dossier
        const fetchMissions = async () => {
            try {
                const getMissionsResponse: HTTPResponse = await toast.promise(
                    ApiCallConstructor({
                        endpoint: `/me/dossiers/${dossier.DOS_REF}/missions`,
                        method: 'GET',
                    }),
                    {
                        pending: `Récupération des missions en cours`,
                        error: { render({ data }) { return `Erreur lors de la récupération des missions : ${(data as HTTPResponse).error}` } },
                    });

                // Store chemises
                const tmpMissions: Array<Mission> = getMissionsResponse.result as Array<Mission>;
                setMissions(tmpMissions);
            } catch (err: any) {
                // Handle redirection for session expiration
                if (err.error === ErrorForUsr.NotSignedIn)
                    signOut((cabAlias) => navigate(`/${cabAlias}/login`));

                console.error(err.error)
            }
        }
        fetchMissions().catch((err) => displayAlert({ message: err.error, type: AlertType.error })); // Retrieve Missions


        // If feature is ON, get upcoming actions from BACK or localstorage (or display errr and handle session expiration)
        if (cabinet.permissions?.isPlanningEnabled) {
            fetchUpcomingActions().then((actions) => setUpcomingActions(actions))
                .catch((err: any) => {
                    displayAlert({ message: err.error, type: AlertType.error })
                    if (err.error === ErrorForUsr.NotSignedIn)
                        signOut((cabAlias) => navigate(`/${cabAlias}/login`));
                });
        }
    }, [dossier, navigate, cabinet.permissions.isPlanningEnabled]);

    // Store UI Chemise when treeview is updated
    useEffect(() => {
        setTreeviewChemises(chemises);
        // Store top level chemises as UI
        setTopLevelChemises(chemises.filter((el) => { return (((el.PAR_REFPERE === 0) || (el.PAR_REFPERE === null)) && el.MIS_REF === null) }));
    }, [chemises])


    // Turning OFF eslint since we're updating a state based on its own value
    // and it would cause an infinite loop
    /* eslint-disable react-hooks/exhaustive-deps */

    // Set the new selected chemise when topChemise has been edited
    useEffect(() => {
        if (topChemise?.PAR_REF) {

            // Update the main chemise only if the user selected a top level chemise
            //if (!chemise.PAR_REF_CHEMIN || chemise.PAR_REF_CHEMIN.indexOf(topChemise.PAR_REF.toString()) === undefined) {
            setChemise(topChemise as Chemise);
            //}

            if (mission.MIS_REF !== topChemise.MIS_REF)
                setMission({} as Mission);
        } else
            setChemise({} as Chemise);

        console.log('chemise : ', topChemise?.PAR_REF);
        console.log('mission : ', mission?.MIS_REF);
    }, [topChemise]);

    // Handle conditional components rendering
    const [componentRenderedIndex, setComponentRenderedIndex] = useState<FolderRenderableComponent>(FolderRenderableComponent.FolderHome);

    useEffect(() => {
        if (chemise.PAR_REFPERE && !mission.MIS_REF) {
            const matchedParRef = chemise.PAR_REF_CHEMIN.match(/[^\\]+/)
            const topParRef = matchedParRef && matchedParRef.length > 0 ? matchedParRef[0] : 0;

            if (topChemise.PAR_REF !== Number(topParRef)) {
                setTopChemise(topLevelChemises.filter((el) => { return el.PAR_REF === topParRef })[0]);
            }
        }

        // Render the chemise content if a chemise was just selected
        //if (chemise.PAR_REF && componentRenderedIndex !== FolderRenderableComponent.ChemiseDetails) setComponentRenderedIndex(FolderRenderableComponent.ChemiseDetails);
        // otherwise just render the folder's home page
        //else if (!chemise.PAR_REF && !mission.MIS_REF) setFolderComponentRendered(getComponentFromIndex(FolderRenderableComponent.FolderHome));
    }, [chemise, componentRenderedIndex, mission, topLevelChemises]);

    // Set chemise to null when a new mission has been selected

    useEffect(() => {
        if (mission && mission.MIS_REF && topChemise && mission.MIS_REF !== topChemise.MIS_REF)
            setTopChemise({} as Chemise);
    }, [mission])
    /* eslint-enable react-hooks/exhaustive-deps */


    // Filter the new documents in order to retrieve only those matching DOS_REF
    useEffect(() => {
        if (newDocuments && newDocuments.length > 0)
            setNewDocsDossier(newDocuments.filter(doc => doc.DOS_REF === dossier.DOS_REF));
    }, [newDocuments, dossier]);

    // Filter the new actions in order to retrieve only those matching DOS_REF
    useEffect(() => {
        if (upcomingActions && upcomingActions.length > 0)
            setUpcomingActionsDossier(upcomingActions.filter(action => action.DOS_REF === dossier.DOS_REF));
    }, [upcomingActions, dossier]);

    useEffect(() => {
        setTopChemise({} as Chemise);
        setComponentRenderedIndex(FolderRenderableComponent.FolderHome);
    }, [dossier])




    /**
     * Get path of a parRef in order to render it
     * @param {number} parRef 
     * @returns {string}
     */
    const getLocation = useCallback((parRef: number) => {
        // get chemise
        const chemiseSearch = treeviewChemises.filter((chemise) => chemise.PAR_REF === parRef);
        const chemise = chemiseSearch && chemiseSearch.length > 0 ? chemiseSearch[0] : {} as Chemise;

        // format the location
        let location = chemise.PAR_CHEMIN ? chemise.PAR_CHEMIN.replaceAll('\\\\', '\\') : '';
        console.log('location : ', location);

        // Add mission if provided
        if (chemise.MIS_REF && chemise.MIS_REF > 0) {
            const missionSearch = missions.filter((mission) => mission.MIS_REF === chemise.MIS_REF);
            const mission = missionSearch && missionSearch.length > 0 ? missionSearch[0] : {} as Mission;
            location = mission.MIS_LIBELLE + '\\' + location;
        }

        return location;
    }, [missions, treeviewChemises]);

    // newDocsTableRows reload
    const [newDocsTableRows, setNewDocsTableRows] = useState<TableRow[]>([]);
    useEffect(() => {
        // Format the data as needed by the table
        const tableRows: TableRow[] = [];
        newDocsDossier.forEach(newDoc => {
            tableRows.push({
                'id': newDoc.DOC_REF,
                'Date': newDoc.DOC_DATE,
                'Type': getExtFromFilename(newDoc.DOC_FILE),
                'Nom': newDoc.DOC_NOM || '',
                'Emplacement': getLocation(newDoc.PAR_REF)
            });
        });
        setNewDocsTableRows(tableRows);
    }, [newDocsDossier, getLocation])


    // upcomingActionsTableRows reload
    const [upcomingActionsTableRows, setUpcomingActionsTableRows] = useState<TableRow[]>([]);
    useEffect(() => {
        // Format the data as needed by the table
        const tableRows: TableRow[] = [];
        upcomingActionsDossier.forEach(upcomingAction => {
            tableRows.push({
                'id': upcomingAction.ACT_ID,
                'Date': upcomingAction.ACT_DATE,
                'Lieu': upcomingAction.ACT_LIEU ?? '',
                'Descriptif': upcomingAction.ACT_LIBELLE,
                'Intervenant': upcomingAction.ACT_ACTEUR,
                'Commentaire': upcomingAction.ACT_DESCRIPTION ?? ''
            });
        });
        setUpcomingActionsTableRows(tableRows);
    }, [upcomingActionsDossier, getLocation]);

    /*
     * UI Render functions
     */

    /**
     * Returns the HTML content to render depending on which FolderComponent is asked
     * @author Mickaël "Naxe67" APPEL
     * @since 2024-03-29
     * @returns {React.ReactNode}
     */
    const getComponentFromIndex = (index: FolderRenderableComponent): React.ReactNode => {
        switch (index) {
            // Display the ChemiseDetails component which will display the content of a selected chemise
            case FolderRenderableComponent.ChemiseDetails:
                return (
                    <ChemiseDetails key={`details-dos${dossier.DOS_REF || 0}-mis${mission.MIS_REF || 0}-che${chemise.PAR_REF || 0}`} dossier={dossier} treeviewChemises={treeviewChemises} chemise={chemise} mission={mission} chemiseHandler={setChemise} />
                );


            // Display the Deposit component which will display a history of sent documents
            case FolderRenderableComponent.SentDocuments:
                return (<SentDocuments dossier={dossier}></SentDocuments>);

            case FolderRenderableComponent.Events:
                return (<EventsTable dossier={dossier}></EventsTable>)

            // Display kind of a 'Home' Page specific to the selected folder
            case FolderRenderableComponent.FolderHome:
            default:

                // Download documents the user has selected
                // BE AWARE : Downloading more than 10 documents at the same time seems to
                // skip the download of some of the wanted documents. In order to prevent that
                // You will have to uncomment the timeout of 1s every 10 downloads
                // But this is useful only if the pagination is showing more than 10 documents at a time
                const DownloadSelectedDocs = async (selectedRow: TableRow[]) => {
                    if (isDownloading) return; // Prevent multiple download

                    try {
                        // Must download a SELECTED document
                        if (selectedRow.length <= 0)
                            return;

                        setIsDownloading(true);

                        // Download each document one by one
                        await toast.promise(
                            Promise.all(
                                selectedRow.map(async row => {
                                    try { await downloadDocument(newDocsDossier.filter(doc => doc.DOC_REF === row.id)[0], navigate) }
                                    catch (error) { throw error; }
                                }
                                )
                            ),
                            {
                                pending: 'Préparation de votre téléchargement',
                                error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
                                success: 'Document(s) téléchargé(s) avec succès'
                            });
                    } catch (error) {
                        console.error(error);
                    }

                    setIsDownloading(false);
                };

                return (
                    /* New published documents table */
                    <div className='flex flex-col h-full'>
                        <div className='m-4 my-2 flex-grow overflow-y-auto'>
                            {/* Title */}
                            <div className="font-bold my-2">
                                <span className='text-md lg:text-lg'><FontAwesomeIcon className={`w-6 h-6 lg:w-8 lg:h-8`} icon={faBell} style={{ color: cabinet.color + 'CF' }} /> Nouveaux documents</span>
                                <hr className={`mt-1 border-1 opacity-100 rounded-lg w-[30%]`} style={{ borderColor: cabinet.color }} />
                            </div>

                            {/* Table */}
                            <BCTable key={'documents-' + newDocsTableRows.length} emptyMsg={'Aucun nouveau document n\'a été publié'} paginationMsg={'Document [0] à [1] sur [2]'} sortSettings={{ colName: 'Date', order: SortOrder.desc }}
                                columns={[
                                    { 'name': 'id', show: false }, // ID
                                    { 'name': 'Date', show: true, className: 'text-left w-[100px]', formatBeforeRender: (row: TableRow, colKey: string) => dateToDDMMYYYY(row[colKey]), sort: SortType.Date, usedForResearch: true },
                                    { 'name': 'Type', show: true, className: 'text-left w-[80px]', formatBeforeRender: (row: TableRow, colKey: string) => getTypeImg(row[colKey], true), sort: SortType.Alphanumeric, usedForResearch: true },
                                    { 'name': 'Nom', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                                    { 'name': 'Emplacement', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true }
                                ]} data={newDocsTableRows} selection={true} buttons={[{ name: 'Télécharger', icon: faDownload, action: (selectedRows: TableRow[]) => { DownloadSelectedDocs(selectedRows); } }]} />

                            {/* Upcoming Actions Title */}
                            {cabinet.permissions?.isPlanningEnabled ? <div className="font-bold mb-2 mt-8 grow">
                                <div className='text-md lg:text-lg text-black'><FontAwesomeIcon className={`w-6 h-6 lg:w-8 lg:h-8`} icon={faBell} style={{ color: cabinet.color + 'CF' }} /> Evènements à venir</div>
                                <hr className={`mt-1 border-1 opacity-100 rounded-lg w-[30%]`} style={{ borderColor: cabinet.color }} />
                            </div> : <></>}

                            {/* Upcoming Actions Table */}
                            {cabinet.permissions?.isPlanningEnabled ? <BCTable id="actions-table" key={'actions-' + upcomingActionsTableRows.length} customKey='actions' emptyMsg={'Aucun évènement à venir'} paginationMsg={'Evénement [0] à [1] sur [2]'} sortSettings={{ colName: 'Date', order: SortOrder.desc }}
                                columns={[
                                    { 'name': 'id', show: false }, // ID
                                    { 'name': 'Date', show: true, className: 'text-left w-[150px]', formatBeforeRender: (row: TableRow, colKey: string) => dateToDDMMYYYY(row[colKey], true), sort: SortType.Date, usedForResearch: true },
                                    { 'name': 'Lieu', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                                    { 'name': 'Descriptif', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                                    { 'name': 'Intervenant', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                                    { 'name': 'Commentaire', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                                ]} data={upcomingActionsTableRows} selection={false}
                                buttons={[{ name: 'Imprimer', icon: faPrint, action: async (selectedRows: TableRow[]) => { printElement(document.getElementById('actions-table'), { pageTitle: `Evènements à venir (${getDossierNameFormated(dossier)})`, logoBase64: cabinet.logo, orientation: 'landscape' }); }, withoutSelection: true }]} /> : <></>}

                        </div>

                        {/* Advertisment */}
                        <Advertising className='py-4  max-h-[35vh]' />
                    </div>
                );
        }
    }

    return (
        <div className='flex flex-col flex-1 h-full'>

            {/* Header contaning logo + folderinput + profile */}
            <Header key={'header' + dossier.DOS_REF} showFolderInput={true} dossier={dossier} onFolderChange={setDossier} />


            {/* Rest of the page */}
            <div className='flex flex-row flex-grow w-full'>

                { /* SideBar */}
                <SideBar key={`sb-dos${dossier.DOS_REF}-${missions.length}-mis${mission.MIS_REF || 0}-${topLevelChemises.length}-che${chemise.PAR_REF}`}
                    dossier={dossier} changeRenderedComponent={setComponentRenderedIndex} expanded={sidebarExpanded} setExpanded={setSidebarExpanded}
                    chemises={topLevelChemises} chemise={topChemise} chemiseHandler={setTopChemise} chemisesExpanded={chemisesExpanded || false} setChemisesExpanded={setChemisesExpanded}
                    missions={missions} mission={mission} missionHandler={setMission} missionsExpanded={missionsExpanded || false} setMissionsExpanded={setMissionsExpanded} />

                {/* Folder's content */}
                <div key={dossier.DOS_REF} className='w-full flex-grow shadow overflow-auto h-[90vh]'>
                    {getComponentFromIndex(componentRenderedIndex)}
                </div>

            </div>

        </div>
    );
};